import { Card, Spinner } from "react-bootstrap";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, TooltipProps } from "recharts";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { STYLE, COLOR } from "../../../../config/constraits";
import { addFilter, saveBeforeFilter } from "../../../../redux/filtersSlice";
import { generateCsvFile } from "../../../../utils/downloadCsvFile";
import ChartShareActions from "../../../../components/ChartShareActions";
import { RootState } from "../../../../redux/store";
import { useImprovementPlanStatusAndTypeBarChart } from "../../hooks/useImprovementPlanStatusAndTypeBarChart";
import { AuditActionEnum } from "../../../auditLogs/types";
import { ImprovementPlanByStatusAndTypeLineChartDataDto } from "../../types";

interface CustomizedAxisTickProps {
    x: number; // Coordenada X de la posición del tick
    y: number; // Coordenada Y de la posición del tick
    labelText: string;
}

interface ImprovementPlanStatusAndTypeBarChartProps {
    setMessage: (msg: string) => void;
}

const CustomizedAxisTick: React.FC<CustomizedAxisTickProps> = ({ x, y, labelText }) => {
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-35)">
                {labelText}
            </text>
        </g>
    );
};


const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
    const { t } = useTranslation();

    if (active && payload && payload.length) {
        const total =
            (payload.find(p => p.dataKey === "inDefinitionCount")?.value || 0) +
            (payload.find(p => p.dataKey === "scheduledCount")?.value || 0) +
            (payload.find(p => p.dataKey === "inProgressCount")?.value || 0) +
            (payload.find(p => p.dataKey === "finishedCount")?.value || 0);

        return (
            <div style={{ backgroundColor: "#fff", border: "1px solid #ccc", padding: "10px" }}>
                <p><strong>{t(label)}</strong>: {total} </p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color, margin: 0 }}>
                        {entry.name}: {entry.value}
                    </p>
                ))}
                <hr />
            </div>
        );
    }

    return null;
};

export default function ImprovementPlanStatusAndTypeBarChart({ setMessage }: ImprovementPlanStatusAndTypeBarChartProps) {
    const { t } = useTranslation('improvementPlan');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { data, isLoading, error } = useImprovementPlanStatusAndTypeBarChart();
    const feelingchartRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.improvementPlans.selectedFilters);

    useEffect(() => {
        if (error) {
            const messageError = (error as any)?.response?.data?.message || (error as Error).message;
            setMessage(messageError);
        }
    }, [error, setMessage]);

    const handleBarClick = (e: string) => {
        const filter = {
            name: 'ImprovementPlanType',
            values: [e]
        };
        dispatch(saveBeforeFilter('improvementPlans'));
        dispatch(addFilter({ table: 'improvementPlans', filter: filter }));
        navigate('/improvement-plans/historic');
    };

    const exportBarChartCsv = async (): Promise<void> => {
        const rows = (data ?? []) as ImprovementPlanByStatusAndTypeLineChartDataDto[];
        generateCsvFile({
            headers: [
                t("improvementPlan:statusAndTypeBarChart.labelTitle"),
                t("improvementPlan:statusAndTypeBarChart.export.headerTotalImprvementPlans"),
                t("improvementPlan:statusAndTypeBarChart.labelInDefinition"),
                t("improvementPlan:statusAndTypeBarChart.labelScheduled"),
                t("improvementPlan:statusAndTypeBarChart.labelInProgress"),
                t("improvementPlan:statusAndTypeBarChart.labelFinished"),
            ],
            rows,
            mapRow: (item) => {
                const totalImprovementPlans = (item.inDefinitionCount ?? 0) + (item.scheduledCount ?? 0) + (item.inProgressCount ?? 0) + (item.finishedCount ?? 0);
                return [
                    item.typeDescription ?? "",
                    totalImprovementPlans,
                    item.inDefinitionCount ?? 0,
                    item.scheduledCount ?? 0,
                    item.inProgressCount ?? 0,
                    item.finishedCount ?? 0,
                ];
            },
            fileBaseName: "improvement_plans_status_by_type",
            trailingSemicolon: true,
        });
    };

    return (
    <>
        {isLoading && (
            <div className="overlay-spinner">
                <Spinner animation="border" variant="light" />
            </div>
        )}
        <div ref={feelingchartRef}>
            <Card
                style={{
                    backgroundColor: "#F5F5F5",
                    border: "none",
                    borderRadius: STYLE.BORDER_RADIUS,
                    height: "400px"
                }}
                className="p-3 d-flex justify-content-center align-items-center text-center"
            >
                <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                    <h4>{t("improvementPlan:statusAndTypeBarChart.labelTitle")}</h4>
                    <div className="d-flex align-items-center">
                        <ChartShareActions
                            chartRef={feelingchartRef}
                                title={t("improvementPlan:statusAndTypeBarChart.labelTitle")}
                            filters={selectedFilters}
                            t={t}
                            exportCsvAction={exportBarChartCsv}
                            exportCsvAudit={{
                                entity: "ImprovementePlansDashboardByTypeAndStatus",
                                action: AuditActionEnum.ExportCsv,
                                source: window.location.pathname,
                                description: "CSV export of improvement plan by type and status bar chart",
                                filters: selectedFilters
                            }}
                        />
                    </div>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        width={500}
                        height={300}
                        data={data}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 70,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="typeDescription" angle={60} height={120} tick={({ x, y, payload }) => <CustomizedAxisTick x={x} y={y} labelText={t(payload.value)} />} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend verticalAlign="bottom" align="center" height={36} />
                            <Bar name={t("improvementPlan:statusAndTypeBarChart.labelInDefinition")} dataKey="inDefinitionCount" onClick={(barData) => handleBarClick(barData.typeId)} stackId="a" fill={"#6258CC"} />
                            <Bar name={t("improvementPlan:statusAndTypeBarChart.labelScheduled")} dataKey="scheduledCount" onClick={(barData) => handleBarClick(barData.typeId)} stackId="a" fill={"#C0A3FF"} />
                            <Bar name={t("improvementPlan:statusAndTypeBarChart.labelInProgress")} dataKey="inProgressCount" onClick={(barData) => handleBarClick(barData.typeId)} stackId="a" fill={"#FFC876"} />
                            <Bar name={t("improvementPlan:statusAndTypeBarChart.labelFinished")} dataKey="finishedCount" onClick={(barData) => handleBarClick(barData.typeId)}  stackId="a" fill={COLOR.VERD} />
                    </BarChart>
                </ResponsiveContainer>
            </Card>
        </div>     
    </>
    );
}
